import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

/**
 * Reemplazo de `window.confirm()` con estilo shadcn.
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, description, confirmLabel, destructive });
 *   if (!ok) return;
 */
const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "¿Estás seguro?",
  description: "",
  confirmLabel: "Continuar",
  cancelLabel: "Cancelar",
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve }
  const resolverRef = useRef(null);

  const ask = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ ...DEFAULTS, ...opts });
    });
  }, []);

  const handleClose = (value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(value);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) handleClose(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className={state?.destructive ? "flex items-center gap-2 text-red-600 dark:text-red-400" : "flex items-center gap-2"}>
              {state?.destructive && <AlertTriangle className="w-5 h-5" />}
              {state?.title}
            </AlertDialogTitle>
            {state?.description && (
              <AlertDialogDescription className="whitespace-pre-line">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleClose(false)} data-testid="confirm-cancel-btn">
              {state?.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleClose(true)}
              data-testid="confirm-accept-btn"
              className={state?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {state?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm debe usarse dentro de <ConfirmProvider>");
  return ctx;
}
